import React, { useState, useEffect } from "react";
import { autoSignIn, confirmSignUp, resendSignUpCode } from "aws-amplify/auth";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextInput,
  Button,
  Stack,
  Text,
  Box,
  Group,
  Title,
} from "@mantine/core";
import styles from "./shared/AuthLayout.module.css";

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const username = location.state?.username;
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    () => location.state?.infoMessage || "",
  );

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleResendCode = async () => {
    if (countdown > 0) return;

    try {
      await resendSignUpCode({ username });
      setCountdown(60);
      setError("");
      setSuccessMessage("A new verification code has been sent to your email");

      setTimeout(() => setSuccessMessage(""), 60000);
    } catch (error) {
      setError(error.message || "Failed to send verification code");
      setSuccessMessage("");
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await confirmSignUp({
        username: username,
        confirmationCode: code,
      });

      if (
        response.isSignUpComplete &&
        response.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        const nextStep = await autoSignIn();
        if (nextStep.signInStep === "DONE") {
          navigate("/account-creation");
        } else {
          navigate("/login", {
            state: {
              successMessage:
                "Email verified!  Please sign in using your new credentials.",
            },
          });
        }
      }
    } catch (error) {
      setError(error.message || "Failed to verify email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className={styles.pageWrapper}>
      {/* Header with Logo */}
      <header className={styles.header}>
        <img
          src="/images/pagoda-logo-horizontal.png"
          alt="Pagoda"
          className={styles.logo}
        />
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Title Section */}
        <Stack gap="xs" className={styles.titleSection}>
          <Title order={1} className={styles.title}>
            Verify your email
          </Title>
          <Text size="sm" c="dimmed" className={styles.subtitle}>
            We've sent a verification code to {username || "your email"}
          </Text>
        </Stack>

        {/* Form Card */}
        <Box className={styles.formCard}>
          {error && <Text className={styles.errorMessage}>{error}</Text>}

          {successMessage && (
            <Text className={styles.successMessage}>{successMessage}</Text>
          )}

          <form onSubmit={handleVerification}>
            <Stack className={styles.formStack}>
              <TextInput
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                radius="sm"
                size="md"
                required
                styles={{
                  label: {
                    fontWeight: 500,
                    marginBottom: 8,
                    color: "#101820",
                  },
                }}
              />

              <Stack gap="xs">
                <Button
                  type="submit"
                  loading={isLoading}
                  fullWidth
                  radius="sm"
                  size="md"
                  className={styles.submitButton}
                >
                  Verify Email
                </Button>

                <button
                  type="button"
                  onClick={handleResendCode}
                  className={`${styles.actionLink} ${countdown > 0 ? styles.actionLinkDisabled : ""}`}
                  disabled={countdown > 0}
                >
                  {countdown > 0
                    ? `Send new confirmation code (${countdown}s)`
                    : "Send new confirmation code"}
                </button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text size="sm" className={styles.copyright}>
          © Copyright {new Date().getFullYear()}, Pagoda Labs, Inc.
        </Text>
        <Group gap="md" className={styles.footerLinks}>
          <a
            href="https://pagoda.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy Policy
          </a>
          <a
            href="https://pagoda.co/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Terms & Conditions
          </a>
        </Group>
      </footer>
    </Box>
  );
}

export default VerifyEmail;
